// bigBoxButtons.component.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { jwtDecode } from "jwt-decode";
import { useDataLayer } from "../../../../Contexts/DataLayer.context.jsx";
import { usePlayer } from "../../../../Contexts/playerData.context.jsx";
import { useNavBar } from "../../../../Contexts/navbar.items.context.jsx";
import { getPlayersFromList, fetchDataQuery } from "../../../../utils/functions.ts";

const LOCAL_STORAGE_LIST = "FBX_SELECTED_LIST";

const BigBoxButtons = ({
  lists = [],
  shareLists = [],
  sharedQueries = [],
  disabled = false,
  disabledReason = "Unsaved changes. Please save first.",
  // ✅ mask this button strip (L1 / L2 / SQ)
  mask = false,
  maskReason = "Unsaved changes. Please save first.",
  onLoaded = null,
}) => {
  const [openMenu, setOpenMenu] = useState(null); // "L1" | "L2" | "SQ" | null
  const [loadingKey, setLoadingKey] = useState(null);
  const wrapperRef = useRef(null);

  const { updateWhichOneLiner } = useDataLayer();
  const { setPlayerData, setSavedOriginalPlayerData, setActivePlayer, setPlayerID, setListLoadID } =
    usePlayer();

  // ✅ DO NOT control activeSideButton here — keep Notes/Reports open
  const { updateSearchedListName, updateIsPillSelected } = useNavBar();

  useEffect(() => {
    if (disabled || mask) setOpenMenu(null);
  }, [disabled, mask]);

  useEffect(() => {
    const handleDown = (e) => {
      if (disabled || mask) return;
      if (!wrapperRef.current) return;
      if (!wrapperRef.current.contains(e.target)) setOpenMenu(null);
    };
    document.addEventListener("mousedown", handleDown);
    return () => document.removeEventListener("mousedown", handleDown);
  }, [disabled, mask]);

  const dataByButton = useMemo(() => {
    return {
      L1: { type: "lists", title: "Lists", data: Array.isArray(lists) ? lists : [] },
      L2: { type: "shareLists", title: "Share Lists", data: Array.isArray(shareLists) ? shareLists : [] },
      SQ: { type: "sharedQueries", title: "Saved Queries", data: Array.isArray(sharedQueries) ? sharedQueries : [] },
    };
  }, [lists, shareLists, sharedQueries]);

  const getKey = (item, fallbackIdx) => {
    if (!item || typeof item !== "object") return `k-${fallbackIdx}`;
    return (
      item.ID ??
      item.id ??
      item.value ??
      item.key ??
      item.listID ??
      item.listId ??
      item.queryID ??
      `k-${fallbackIdx}`
    );
  };

  const getListId = (item) => {
    if (!item || typeof item !== "object") return null;
    return item.listId ?? item.listID ?? item.id ?? item.ID ?? null;
  };

  const getListName = (item) => {
    if (!item) return "";
    if (typeof item === "string" || typeof item === "number") return String(item);
    return item.listName ?? item.name ?? item.label ?? item.title ?? item.queryName ?? "";
  };

  const getListTotal = (item) => {
    if (!item || typeof item !== "object") return null;
    const v = item.listTotal ?? item.total ?? item.count ?? null;
    if (v === null || v === undefined || v === "") return null;
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  };

  const getCreated = (item) => {
    if (!item || typeof item !== "object") return "";
    return item.dateCreated ?? item.createdAt ?? item.created ?? "";
  };

  const getDisplayLabel = (btn, item) => {
    const name = getListName(item);
    const total = getListTotal(item);
    const created = getCreated(item);

    // L1 / L2: "Name [Total]"
    if (btn === "L1" || btn === "L2") {
      return total === null ? name : `${name} [${total}]`;
    }

    // SQ: "Name [Total] - Created"
    if (btn === "SQ") {
      let label = name || "Saved Query";
      if (total !== null) label += ` [${total}]`;
      if (created) label += ` - ${created}`;
      return label;
    }

    return name || "";
  };

  const isPlaceholder = (item) => {
    const id = String(getListId(item) ?? "");
    const name = String(item?.listName ?? "").toLowerCase();
    return id === "0" || name === "no list data";
  };

  const toggle = (btn) => {
    if (disabled || mask) return;
    setOpenMenu((prev) => (prev === btn ? null : btn));
  };

  const loadListById = async (listId, listNameForHeader) => {
    const cleanId = String(listId ?? "").trim();
    if (!cleanId) return false;

    setLoadingKey(cleanId);

    try {
      localStorage.setItem(LOCAL_STORAGE_LIST, cleanId);

      updateWhichOneLiner(null);

      const token = localStorage.getItem("user2026");
      const theUser = token ? jwtDecode(token) : null;

      const newPlayers = await getPlayersFromList(cleanId, theUser?.team, theUser?.scout);

      setPlayerData(newPlayers);
      setSavedOriginalPlayerData(newPlayers);

      const first = newPlayers?.[0] || null;
      setPlayerID(first?.FBXID || "");
      setActivePlayer(first || []);

      setListLoadID(Math.floor(Math.random() * (1000000 - 1000 + 1)) + 1000);

      if (listNameForHeader) updateSearchedListName("Current List: " + listNameForHeader);

      updateIsPillSelected(false);
      return true;
    } catch (e) {
      console.error("BigBoxButtons loadListById failed:", e);
      return false;
    } finally {
      setLoadingKey(null);
    }
  };

  const loadSavedQueryById = async (listId, listNameForHeader) => {
    const cleanId = String(listId ?? "").trim();
    if (!cleanId) return false;

    setLoadingKey(cleanId);

    try {
      updateWhichOneLiner(null);

      const token = localStorage.getItem("user2026");
      const theUser = token ? jwtDecode(token) : null;

      const newPlayers = await fetchDataQuery(cleanId, theUser?.scout, theUser?.team);

      setPlayerData(newPlayers);
      setSavedOriginalPlayerData(newPlayers);

      const first = newPlayers?.[0] || null;
      setPlayerID(first?.FBXID || "");
      setActivePlayer(first || []);

      setListLoadID(Math.floor(Math.random() * (1000000 - 1000 + 1)) + 1000);

      if (listNameForHeader) updateSearchedListName("Saved Query: " + listNameForHeader);

      updateIsPillSelected(false);
      return true;
    } catch (e) {
      console.error("BigBoxButtons loadSavedQueryById failed:", e);
      return false;
    } finally {
      setLoadingKey(null);
    }
  };

  const handlePick = async (btn, item) => {
    setOpenMenu(null);

    if (!item || typeof item !== "object") return;

    if (btn !== "SQ" && isPlaceholder(item)) return;

    const listId = getListId(item);
    if (!listId) return;

    const listName = item.listName ?? getListName(item);

    if (btn === "SQ") {
      const loaded = await loadSavedQueryById(listId, listName);
      if (loaded && typeof onLoaded === "function") onLoaded({ button: btn, listId, listName });
      return;
    }

    const loaded = await loadListById(listId, listName);
    if (loaded && typeof onLoaded === "function") onLoaded({ button: btn, listId, listName });
  };

  const renderMenu = (btn) => {
    if (openMenu !== btn) return null;

    const meta = dataByButton[btn];
    const rows = meta?.data ?? [];

    return (
      <div
        className="dropdown-menu show p-1 shadow"
        style={{
          display: "block",
          position: "absolute",
          top: "calc(100% + 12px)",
          left: 0,
          minWidth: "320px",
          maxHeight: "320px",
          overflowY: "auto",
          zIndex: 9999,
        }}
      >
        <div className="px-2 py-1 border-bottom small text-muted">
          <strong>{meta.title}</strong>
        </div>

        {rows.length === 0 ? (
          <div className="px-2 py-2 text-muted small">
            <em>No items.</em>
          </div>
        ) : (
          rows.map((item, idx) => {
            const listId = getListId(item);
            const disabledItem = (btn !== "SQ" && isPlaceholder(item)) || (!listId && btn !== "SQ");
            const label = getDisplayLabel(btn, item);

            return (
              <button
                key={getKey(item, idx)}
                type="button"
                className="dropdown-item py-1 fs-7"
                onClick={() => handlePick(btn, item)}
                title={label}
                disabled={disabledItem}
                style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}
              >
                {label}
              </button>
            );
          })
        )}
      </div>
    );
  };

  const isLoading = Boolean(loadingKey);

  return (
    <div ref={wrapperRef} className="d-flex gap-1 position-relative" style={{ width: "fit-content" }}>
      <div className="position-relative">
        <button
          type="button"
          className={`btn btn-purple fs-7 ${openMenu === "L1" ? "big-box-list-button-open" : ""}`}
          style={{ width: "55px" }}
          onClick={() => toggle("L1")}
          disabled={isLoading || disabled || mask}
          title={isLoading ? "Loading list..." : "My Lists"}
        >
          L1
        </button>
        {renderMenu("L1")}
      </div>

      <div className="position-relative">
        <button
          type="button"
          className={`btn btn-purple fs-7 ${openMenu === "L2" ? "big-box-list-button-open" : ""}`}
          style={{ width: "55px" }}
          onClick={() => toggle("L2")}
          disabled={isLoading || disabled || mask}
          title={isLoading ? "Loading list..." : "Shared Lists"}
        >
          L2
        </button>
        {renderMenu("L2")}
      </div>

      <div className="position-relative">
        <button
          type="button"
          className={`btn btn-purple fs-7 ${openMenu === "SQ" ? "big-box-list-button-open" : ""}`}
          style={{ width: "55px" }}
          onClick={() => toggle("SQ")}
          disabled={isLoading || disabled || mask}
          title={disabled || mask ? (maskReason || disabledReason) : "Saved Queries"}
        >
          SQ
        </button>
        {renderMenu("SQ")}
      </div>

      {/* mask ONLY this strip (parent BigBox also masks the full top bar) */}
      {mask === true && (
        <div
          aria-hidden="true"
          title={maskReason}
          style={{
            position: "absolute",
            inset: 0,
            background: "rgba(255,255,255,0.55)",
            border: "1px solid rgba(220,53,69,0.30)",
            cursor: "not-allowed",
            zIndex: 60,
            pointerEvents: "auto",
          }}
        />
      )}
    </div>
  );
};

export default BigBoxButtons;
